import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Chart } from "react-chartjs-2";
import {
    Chart as ChartJS, BarController, BarElement, CategoryScale, LinearScale,
    RadialLinearScale, PolarAreaController, ArcElement, Tooltip, Legend, Title
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";
import { TreemapController, TreemapElement } from "chartjs-chart-treemap";
import { WordCloudController, WordElement } from "chartjs-chart-wordcloud";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { getResultCharts } from "../../Requests/ProjectRequests";
import { getTeamUuid } from "../../utils/cookies";
import { buildChart, buildWordCloud } from "./chartBuilders";
import { whiteBackgroundPlugin, downloadCanvasPng } from "./chartExport";

ChartJS.register(
    BarController, BarElement, CategoryScale, LinearScale, RadialLinearScale, PolarAreaController,
    ArcElement, Tooltip, Legend, Title, MatrixController, MatrixElement, TreemapController, TreemapElement,
    WordCloudController, WordElement
);

const TALL_TYPES = ["heatmap", "bar", "code_by_attribute", "cooccurrence"];

function FigureFrame({ figure, title, caption, action, children, t }) {
    return (
        <div className="border border-gray-200 rounded-lg bg-white p-4 flex flex-col gap-3">
            <div className="flex justify-between items-start gap-4">
                <div>
                    <h4 className="text-base font-semibold text-gray-900">{t("CategoryResults.Charts.Figure", { n: figure.number })} · {title}</h4>
                    {caption && <p className="text-sm text-gray-600 mt-1">{caption}</p>}
                </div>
                {action}
            </div>
            {children}
            {figure.description && (
                <details className="text-xs text-gray-500">
                    <summary className="cursor-pointer select-none">{t("CategoryResults.Charts.Description")}</summary>
                    <p className="mt-2 whitespace-pre-wrap">{figure.description}</p>
                </details>
            )}
        </div>
    );
}

const PngButton = ({ onClick, small }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex items-center gap-1 bg-black text-white rounded hover:bg-gray-800 whitespace-nowrap ${small ? "px-2 py-0.5 text-xs" : "px-3 py-1 gap-2"}`}
    >
        <ArrowDownTrayIcon className={small ? "w-4 h-4" : "w-5 h-5"} /> PNG
    </button>
);

function ChartCard({ figure, codes, files, subjects, t }) {
    const chartRef = useRef();
    const built = useMemo(() => buildChart(figure, codes, files, subjects), [figure, codes, files, subjects]);
    if (!built) return null;
    const rows = TALL_TYPES.includes(figure.type) ? codes.length : 0;
    const height = figure.type === "wordcloud" ? 440 : Math.max(360, Math.min(900, 40 * rows + 120));
    const title = t(`CategoryResults.Charts.Types.${figure.type}.title`, figure.params || {});

    return (
        <FigureFrame
            figure={figure}
            title={title}
            caption={t(`CategoryResults.Charts.Types.${figure.type}.caption`, figure.params || {})}
            action={<PngButton onClick={() => downloadCanvasPng(chartRef.current, title)} />}
            t={t}
        >
            <div style={{ height }}>
                <Chart ref={chartRef} type={built.type} data={built.data} options={built.options} plugins={[ChartDataLabels, whiteBackgroundPlugin]} />
            </div>
        </FigureFrame>
    );
}

/** One small word cloud per interview/file. */
function WordCloudPerFileCard({ figure, t }) {
    const refs = useRef({});
    const title = t("CategoryResults.Charts.Types.wordcloud_per_file.title");
    return (
        <FigureFrame figure={figure} title={title} caption={t("CategoryResults.Charts.Types.wordcloud_per_file.caption")} t={t}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {figure.files.map(({ file, words }) => {
                    const built = buildWordCloud(words);
                    return (
                        <div key={file} className="border border-gray-100 rounded p-2 flex flex-col gap-1">
                            <div className="flex justify-between items-center gap-2">
                                <span className="text-sm font-medium text-gray-800 truncate" title={file}>{file}</span>
                                <PngButton small onClick={() => downloadCanvasPng(refs.current[file], `${title} - ${file}`)} />
                            </div>
                            <div style={{ height: 260 }}>
                                {words.length > 0
                                    ? <Chart ref={el => { refs.current[file] = el; }} type={built.type} data={built.data} options={built.options} plugins={[whiteBackgroundPlugin]} />
                                    : <div className="h-full flex items-center justify-center text-xs text-gray-400">{t("CategoryResults.Charts.NoWords")}</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </FigureFrame>
    );
}

export default function ResultCharts({ project, codes = [], files = [], subjects = [] }) {
    const { t } = useTranslation();
    const [figures, setFigures] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!project?.uuid) return;
        getResultCharts(project.uuid, getTeamUuid())
            .then(res => setFigures(res.data.figures || []))
            .catch(() => setError(true));
    }, [project?.uuid, codes]); // eslint-disable-line react-hooks/exhaustive-deps

    if (error) return <div className="text-sm text-red-600 bg-red-50 rounded p-3">{t("CategoryResults.Charts.Error")}</div>;
    if (!figures) return <div className="text-sm text-gray-500">{t("CategoryResults.Charts.Loading")}</div>;

    return (
        <div className="flex flex-col gap-4">
            <h3 className="text-lg font-semibold text-gray-800">{t("CategoryResults.Charts.Title")}</h3>
            {figures.map(figure => {
                if (figure.type === "wordcloud" && !figure.words?.length) return null;
                if (figure.type === "wordcloud_per_file") return <WordCloudPerFileCard key={figure.number} figure={figure} t={t} />;
                return <ChartCard key={figure.number} figure={figure} codes={codes} files={files} subjects={subjects} t={t} />;
            })}
        </div>
    );
}
